import React, { useEffect, useState } from 'react';
import { usePlatformStatus, platformState, useStateAnnouncement, STATE_TEXT } from '@/hooks/useSystemStatus';

/**
 * Top marking strip on the public / auth pages: handling marking, platform
 * state and a UTC clock. Content is aggregated from public sources, so the
 * honest marking is TLP:CLEAR. State comes from /api/system/status.
 *
 * Not a live region: a hidden polite region announces the state only when
 * it changes after load; the clock is hidden from assistive tech. Wording
 * matches the StatusStrip (STATE_TEXT) so both bars say the same thing.
 * The signed-in app shows the same information in its StatusStrip instead.
 */
function useUtcClock() {
  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    // Minute resolution is all we print, so tick on the minute boundary.
    let id;
    const schedule = () => {
      id = setTimeout(() => { setTime(new Date()); schedule(); }, 60_000 - (Date.now() % 60_000) + 50);
    };
    schedule();
    return () => clearTimeout(id);
  }, []);
  const hh = String(time.getUTCHours()).padStart(2, '0');
  const mm = String(time.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} UTC`;
}

export default function ClassificationBar({ variant = 'customer' }) {
  const clock = useUtcClock();
  const status = usePlatformStatus();
  const { state, stale } = platformState(status);
  const isAdmin = variant === 'admin';
  const announcement = useStateAnnouncement(state);

  return (
    <div className="is-classification-bar" data-testid="classification-bar">
      <span
        className="is-class-tag"
        title={isAdmin ? 'Administrator console' : 'Traffic Light Protocol: content is from public sources and may be shared freely'}
      >
        {isAdmin ? 'ADMIN' : 'TLP:CLEAR'}
      </span>
      {isAdmin ? (
        <span>Platform console · restricted</span>
      ) : (
        <>
          <span className={`is-class-dot is-dot--${state}`} aria-hidden="true" />
          <span title={STATE_TEXT[state]?.[1]}>
            {STATE_TEXT[state]?.[0] || state}
            {stale && ' · stale'}
          </span>
          <span className="sr-only" aria-live="polite">{announcement}</span>
        </>
      )}
      <span style={{ flex: 1 }} />
      <span className="is-hide-sm" aria-hidden="true">{clock}</span>
    </div>
  );
}
